import { z } from 'zod';
import DOMPurify from 'isomorphic-dompurify';

// Base validation schemas
export const emailSchema = z.string()
  .email("Please enter a valid email address")
  .min(1, "Email is required")
  .max(255, "Email must be less than 255 characters");

export const passwordSchema = z.string()
  .min(8, "Password must be at least 8 characters")
  .max(128, "Password must be less than 128 characters")
  .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/, 
    "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character");

export const nameSchema = z.string()
  .min(1, "Name is required")
  .max(100, "Name must be less than 100 characters")
  .regex(/^[a-zA-Z\s\-'\.]+$/, "Name can only contain letters, spaces, hyphens, apostrophes, and periods");

export const phoneSchema = z.string()
  .optional()
  .refine((val) => !val || /^[\+]?[1-9][\d]{0,15}$/.test(val), "Please enter a valid phone number");

export const zipCodeSchema = z.string()
  .optional()
  .refine((val) => !val || /^[0-9]{5}(-[0-9]{4})?$/.test(val), "Please enter a valid ZIP code");

export const currencySchema = z.number()
  .min(0, "Amount must be positive")
  .max(999999999.99, "Amount is too large");

export const optionalCurrencySchema = z.coerce.number()
  .min(0, "Amount must be positive")
  .max(999999999.99, "Amount is too large")
  .optional()
  .or(z.literal("").transform(() => undefined))
  .or(z.null().transform(() => undefined));

export const quantitySchema = z.number()
  .min(0.01, "Quantity must be greater than 0")
  .max(999999999, "Quantity is too large");

export const optionalQuantitySchema = z.coerce.number()
  .min(0, "Quantity cannot be negative")
  .max(999999999, "Quantity is too large")
  .optional()
  .or(z.literal("").transform(() => undefined))
  .or(z.null().transform(() => undefined));

export const skuSchema = z.string()
  .optional()
  .refine((val) => !val || /^[A-Z0-9\-]+$/.test(val), "SKU can only contain uppercase letters, numbers, and hyphens");

// Authentication schemas
export const signInSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "Password is required")
});

export const signUpSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  firstName: nameSchema,
  lastName: nameSchema
});

// Customer validation schema
export const customerSchema = z.object({
  name: z.string().min(1, "Company name is required").max(255, "Company name too long"),
  email: emailSchema.optional().or(z.literal('')),
  phone: phoneSchema,
  address: z.string().max(500, "Address too long").optional(),
  city: z.string().max(100, "City name too long").optional(),
  state: z.string().max(50, "State name too long").optional(),
  zipCode: zipCodeSchema,
  country: z.string().min(2, "Country is required").max(3, "Invalid country code"),
  customerType: z.enum(['retail', 'wholesale', 'distributor']),
  creditLimit: optionalCurrencySchema,
  paymentTerms: z.number().min(0).max(365, "Payment terms must be between 0 and 365 days"),
  notes: z.string().max(1000, "Notes too long").optional()
});

// Inventory item validation schema
export const inventoryItemSchema = z.object({
  name: z.string().min(1, "Item name is required").max(255, "Item name too long"),
  description: z.string().max(1000, "Description too long").optional(),
  sku: skuSchema,
  type: z.enum(['raw_material', 'finished_product']),
  unitOfMeasure: z.string().min(1, "Unit of measure is required").max(20, "Unit name too long"),
  unitCost: optionalCurrencySchema,
  sellingPrice: optionalCurrencySchema,
  minStockLevel: optionalQuantitySchema,
  maxStockLevel: optionalQuantitySchema,
  supplierId: z.string().uuid().optional().or(z.literal(''))
});

// Order validation schema
export const orderSchema = z.object({
  customerId: z.string().uuid("Please select a customer"),
  orderDate: z.date(),
  requiredDate: z.date().optional(),
  shippingAmount: optionalCurrencySchema,
  notes: z.string().max(1000, "Notes too long").optional(),
  items: z.array(z.object({
    inventoryItemId: z.string().uuid("Please select an item"),
    quantity: quantitySchema,
    unitPrice: currencySchema
  })).min(1, "At least one item is required")
});

// Invoice validation schema
export const invoiceSchema = z.object({
  customerId: z.string().uuid("Please select a customer"),
  orderId: z.string().uuid().optional().or(z.literal('')),
  invoiceDate: z.date(),
  dueDate: z.date(),
  notes: z.string().max(1000, "Notes too long").optional()
});

// Inventory transaction validation schema
export const inventoryTransactionSchema = z.object({
  inventoryItemId: z.string().uuid("Please select an inventory item"),
  transactionType: z.enum(['received', 'used', 'sold', 'adjusted', 'produced']),
  quantity: quantitySchema,
  unitCost: optionalCurrencySchema,
  referenceNumber: z.string().max(100, "Reference number too long").optional(),
  notes: z.string().max(1000, "Notes too long").optional()
});

// Supplier validation schema
export const supplierSchema = z.object({
  name: z.string().min(1, "Supplier name is required").max(255, "Supplier name too long"),
  contactPerson: nameSchema.optional().or(z.literal('')),
  email: emailSchema.optional().or(z.literal('')),
  phone: phoneSchema,
  address: z.string().max(500, "Address too long").optional(),
  notes: z.string().max(1000, "Notes too long").optional()
});

// Input sanitization functions
export const sanitizeInput = (input: string): string => {
  if (!input) return '';
  
  // Remove any potential XSS vectors
  const sanitized = DOMPurify.sanitize(input, { 
    ALLOWED_TAGS: [], // No HTML tags allowed
    ALLOWED_ATTR: [] // No attributes allowed
  });
  
  // Trim whitespace and normalize
  return sanitized.trim();
};

export const sanitizeHtml = (html: string): string => {
  if (!html) return '';
  
  // Allow basic formatting but remove scripts and dangerous elements
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: []
  });
};

// Object sanitization helper
export const sanitizeObject = <T extends Record<string, any>>(obj: T): T => {
  const sanitized = {} as T;
  
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      sanitized[key as keyof T] = sanitizeInput(value) as T[keyof T];
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      sanitized[key as keyof T] = sanitizeObject(value) as T[keyof T];
    } else {
      sanitized[key as keyof T] = value;
    }
  }
  
  return sanitized;
};

// Rate limiting helper (simple in-memory implementation)
class RateLimiter {
  private attempts: Map<string, { count: number; resetTime: number }> = new Map();
  
  isAllowed(key: string, maxAttempts: number, windowMs: number): boolean {
    const now = Date.now();
    const record = this.attempts.get(key);
    
    if (!record || now > record.resetTime) {
      this.attempts.set(key, { count: 1, resetTime: now + windowMs });
      return true;
    }
    
    if (record.count >= maxAttempts) {
      return false;
    }
    
    record.count++;
    return true;
  }
  
  getRemainingTime(key: string): number {
    const record = this.attempts.get(key);
    if (!record) return 0;
    
    const remaining = record.resetTime - Date.now();
    return Math.max(0, remaining);
  }
}

export const rateLimiter = new RateLimiter();

// Rate limiting constants
export const RATE_LIMITS = {
  AUTH: { maxAttempts: 5, windowMs: 15 * 60 * 1000 }, // 5 attempts per 15 minutes
  API: { maxAttempts: 100, windowMs: 60 * 1000 }, // 100 requests per minute
  SENSITIVE: { maxAttempts: 3, windowMs: 5 * 60 * 1000 } // 3 attempts per 5 minutes
} as const;

// Data Integrity Verification System
interface IntegrityMetadata {
  hash: string;
  timestamp: number;
  version: string;
  fields: string[];
}

interface CriticalData {
  [key: string]: any;
}

// Generate SHA-256 hash of critical data
export const generateDataHash = async (data: CriticalData, fields?: string[]): Promise<string> => {
  try {
    // Select specific fields if provided, otherwise use all data
    const relevantData = fields 
      ? fields.reduce((obj, field) => {
          if (data[field] !== undefined) {
            obj[field] = data[field];
          }
          return obj;
        }, {} as CriticalData)
      : data;
    
    // Normalize data for consistent hashing
    const normalizedData = normalizeForHashing(relevantData);
    const dataString = JSON.stringify(normalizedData);
    
    // Generate SHA-256 hash using Web Crypto API
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(dataString);
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
    
    // Convert to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (error) {
    console.error('Error generating hash:', error);
    throw new Error('Failed to generate data integrity hash');
  }
};

// Normalize data for consistent hashing (sort keys, handle special types)
const normalizeForHashing = (data: any): any => {
  if (data === null || data === undefined) {
    return null;
  }
  
  if (typeof data === 'string' || typeof data === 'number' || typeof data === 'boolean') {
    return data;
  }
  
  if (data instanceof Date) {
    return data.toISOString();
  }
  
  if (Array.isArray(data)) {
    return data.map(normalizeForHashing).sort();
  }
  
  if (typeof data === 'object') {
    const normalized: any = {};
    const sortedKeys = Object.keys(data).sort();
    
    for (const key of sortedKeys) {
      // Skip metadata fields that shouldn't affect integrity
      if (!key.startsWith('_') && key !== 'created_at' && key !== 'updated_at') {
        normalized[key] = normalizeForHashing(data[key]);
      }
    }
    
    return normalized;
  }
  
  return data;
};

// Add integrity check to critical data
export const addIntegrityCheck = async (
  data: CriticalData,
  criticalFields?: string[]
): Promise<CriticalData & { _integrity: IntegrityMetadata }> => {
  const hash = await generateDataHash(data, criticalFields);
  
  const integrityMetadata: IntegrityMetadata = {
    hash,
    timestamp: Date.now(),
    version: '1.0',
    fields: criticalFields || Object.keys(data).filter(key => !key.startsWith('_'))
  };
  
  return {
    ...data,
    _integrity: integrityMetadata
  };
};

// Verify data integrity
export const verifyIntegrity = async (
  data: CriticalData & { _integrity?: IntegrityMetadata }
): Promise<{ isValid: boolean; error?: string }> => {
  if (!data._integrity) {
    return { isValid: false, error: 'No integrity metadata found' };
  }
  
  try {
    const { _integrity, ...dataWithoutMetadata } = data;
    const currentHash = await generateDataHash(dataWithoutMetadata, _integrity.fields);
    
    if (currentHash !== _integrity.hash) {
      return { 
        isValid: false, 
        error: 'Data integrity check failed - data may have been tampered with' 
      };
    }
    
    // Check if data is too old (optional age verification)
    const ageMs = Date.now() - _integrity.timestamp;
    const maxAgeMs = 24 * 60 * 60 * 1000; // 24 hours
    
    if (ageMs > maxAgeMs) {
      return {
        isValid: false,
        error: 'Data integrity metadata is too old'
      };
    }
    
    return { isValid: true };
  } catch (error) {
    return { 
      isValid: false, 
      error: `Integrity verification failed: ${error instanceof Error ? error.message : 'Unknown error'}` 
    };
  }
};

// Critical fields for different data types
export const CRITICAL_FIELDS = {
  FINANCIAL: ['amount', 'total_amount', 'subtotal', 'tax_amount', 'customer_id', 'status'],
  USER_ROLE: ['user_id', 'role', 'is_super_admin'],
  INVENTORY: ['quantity', 'unit_cost', 'inventory_item_id', 'transaction_type'],
  ORDER: ['customer_id', 'total_amount', 'status', 'order_items'],
  INVOICE: ['customer_id', 'total_amount', 'status', 'invoice_items'],
  CONFIG: ['key', 'value', 'category']
} as const;

// Helper functions for specific data types
export const addFinancialIntegrity = async (data: CriticalData) => {
  return addIntegrityCheck(data, [...CRITICAL_FIELDS.FINANCIAL]);
};

export const addUserRoleIntegrity = async (data: CriticalData) => {
  return addIntegrityCheck(data, [...CRITICAL_FIELDS.USER_ROLE]);
};

export const addInventoryIntegrity = async (data: CriticalData) => {
  return addIntegrityCheck(data, [...CRITICAL_FIELDS.INVENTORY]);
};

export const addOrderIntegrity = async (data: CriticalData) => {
  return addIntegrityCheck(data, [...CRITICAL_FIELDS.ORDER]);
};

export const addInvoiceIntegrity = async (data: CriticalData) => {
  return addIntegrityCheck(data, [...CRITICAL_FIELDS.INVOICE]);
};

export const addConfigIntegrity = async (data: CriticalData) => {
  return addIntegrityCheck(data, [...CRITICAL_FIELDS.CONFIG]);
};

// Audit logging for integrity failures
export const logIntegrityFailure = (
  dataType: string,
  operation: string,
  error: string,
  userId?: string
) => {
  const logEntry = {
    timestamp: new Date().toISOString(),
    type: 'INTEGRITY_FAILURE',
    dataType,
    operation,
    error,
    userId,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown'
  };
  
  // Log to console (in production, this would go to your logging service)
  console.error('Data integrity failure detected:', logEntry);
  
  // In a real implementation, you would send this to your audit logging service
  // Example: sendToAuditLog(logEntry);
};